import { ErrorTypes } from "../types";

interface IGalleryResponse {}
interface IGalleryAssetResponse {}

declare interface IMediaValetApiInterface {
  fetchGalleryById(id: string): Promise<IGalleryResponse>;
  fetchGalleryAssetsById(id: string): Promise<IGalleryAssetResponse>;
}

interface IMediaValetApiConfiguration {
  endpoint?: string;
}

const DEFAULT_CONFIGURATION: IMediaValetApiConfiguration = {};

type AuthorizeResponse = {
  apiVersion: string;
  warnings: any[];
  errors: any[];
  payload: { sessionId: string };
  meta: {
    metaInformation: { ElapsedTimeInMS: number };
    createdAt: string;
  };
};

type RequestHeaderOptions = {
  galleryId: string;
};

export function constructRequestHeaders(options: RequestHeaderOptions) {
  const { galleryId } = options;

  const headers = {
    "x-mv-clientsource": "Web",
  } as { [header: string]: string };

  const sessionId = window.localStorage.getItem(
    `wg_session:${galleryId}-ExternalUser`
  );
  if (galleryId && sessionId) {
    headers["x-mv-web-gallery-session-id"] = sessionId;
  }

  return headers;
}

class MediaValetApi implements IMediaValetApiInterface {
  endpoint?: string;

  constructor(config: IMediaValetApiConfiguration = DEFAULT_CONFIGURATION) {
    this.endpoint = config.endpoint;
  }

  async fetchGalleryById(id: string) {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    const response = await fetch(
      new URL(`/public/share/${id}-ExternalUser`, this.endpoint).toString(),
      {
        headers: constructRequestHeaders({ galleryId: id }),
      }
    );
    if (response.status === 401) {
      throw new Error(ErrorTypes.UNAUTHORIZED);
    } else if (response.status >= 400) {
      throw new Error(ErrorTypes.NOT_FOUND);
    }
    return (await response.json()).payload;
  }

  async fetchGalleryAssetsById(id: string): Promise<IAssets> {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    const response = await fetch(
      new URL(
        `/public/share/${id}-ExternalUser/assets`,
        this.endpoint
      ).toString(),
      {
        headers: constructRequestHeaders({ galleryId: id }),
      }
    );

    if (response.status >= 400) {
      throw new Error(ErrorTypes.NOT_FOUND);
    }
    return (await response.json()).payload;
  }

  async fetchAutodeskToken() {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    const response = await fetch(
      new URL(`/public/autodesk/token`, this.endpoint).toString()
    );

    const [token] = (await response.json()) as [IAutodeskAccess];
    return token;
  }

  async fetchWopiPayload(assetId: string, galleryId: string) {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    const response = await fetch(
      new URL(
        `/public/wopiFileInfo/share/${galleryId}-ExternalUser/asset/${assetId}?includeSoftDeleted=true`,
        this.endpoint
      ).toString(),
      { headers: constructRequestHeaders({ galleryId: galleryId }) }
    );

    let wopiPayload = (await response.json()).payload;

    return wopiPayload as IWopiAccess;
  }

  async authorizeGallery(galleryId: string, password: string) {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    try {
      const response = await fetch(
        new URL(
          `/public/share/${galleryId}-ExternalUser/authorize`,
          this.endpoint
        ).toString(),
        {
          method: "POST",
          credentials: "omit",
          headers: {
            "content-type": "application/json",
          },
          body: JSON.stringify(password),
        }
      );

      if (response.status >= 400) {
        return false;
      }

      const {
        sessionId,
      } = ((await response.json()) as AuthorizeResponse)?.payload;

      if (!sessionId) {
        return false;
      }
      window.localStorage.setItem(
        `wg_session:${galleryId}-ExternalUser`,
        sessionId
      );
      return true;
    } catch (err) {
      // Request failed
      console.error(err);
      return false;
    }
  }

  async fetchRenditionKinds(galleryId: string): Promise<IRenditionKind[]> {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    const query = `/public/share/${galleryId}-ExternalUser/renditionKinds`;
    const url = new URL(query, this.endpoint).toString();
    const request = new Request(url);
    const response = await fetch(request, {
      headers: constructRequestHeaders({ galleryId: galleryId }),
    });

    if (response.status >= 400) {
      throw new Error(ErrorTypes.NOT_FOUND);
    }
    return (await response.json()).payload;
  }

  async fetchRendition(
    galleryId: string,
    assetId: string,
    renditionId: string
  ): Promise<IRenditionPayload> {
    if (!this.endpoint) {
      console.warn("No API endpoint.  Cancelling fetch...");
      throw new Error(ErrorTypes.API_NOT_CONFIGFURED);
    }
    const query = `/public/share/${galleryId}-ExternalUser/assets/${assetId}/renditions/${renditionId}`;
    const url = new URL(query, this.endpoint).toString();
    const request = new Request(url);
    const response = await fetch(request, {
      headers: constructRequestHeaders({ galleryId: galleryId }),
    });

    if (response.status >= 400) {
      throw new Error(ErrorTypes.NOT_FOUND);
    }
    return (await response.json()).payload;
  }
}

export default MediaValetApi;
